import { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { VB_TOKENS, VB_FONTS } from "./tokens";
import { clamp, lerp } from "./mathUtils";
import { useReducedMotion } from "./clock";
import { Atmosphere, CameraBreathe } from "./Atmosphere";
import { VaultDoor } from "./VaultDoor";
import { GoldCascade } from "./Particles";
import { computeRoundState, VB_DEFAULT_CASHOUT_T } from "./computeRoundState";
import { formatMultiplier, formatCryptoCompact } from "../../lib/format.js";

// =============================================================================
// SceneWin — production composition for the WIN cinematic.
//
// Maps wall-clock t (0..duration) to a slice of the sequencer round timeline
// (7.6 → 11.0s) so the player sees the cashout flash, door open, gold cascade,
// and the floating "CASHED OUT" banner.
//
// Source ref: Scenes.jsx VaultStage (lines 8-178) + ResultBanner (180-223).
// =============================================================================

export type SceneAudioCueWin = "cashoutFlash" | "doorSwing" | "winSting";

export interface SceneWinProps {
  multiplier: number;
  /** Wall-clock time within the scene, 0..duration. */
  t: number;
  /** Total wall duration in seconds. */
  duration: number;
  width: number;
  height: number;
  vaultSize?: number;
  /** Player's stake in the round currency (defaults to 0.001 BTC). */
  stake?: number;
  /** Currency code shown in the banner subtitle. */
  currency?: string;
  onAudioCue?: (cue: SceneAudioCueWin) => void;
}

export function SceneWin({
  multiplier,
  t,
  duration,
  width,
  height,
  vaultSize = 420,
  stake = 0.001,
  currency = "BTC",
  onAudioCue,
}: SceneWinProps): JSX.Element {
  // ALL hooks must be called unconditionally on every render. `useReducedMotion`
  // flips from false→true after mount when the OS prefers-reduced-motion is
  // enabled; if we returned early on `reduced` BEFORE the useRef/useEffect
  // below, render 1 (reduced=false) would call 3 hooks and render 2
  // (reduced=true) would call 1 — React error #300 ("Rendered fewer hooks
  // than expected"). Keep all hook calls at the top; gate the side-effect
  // body on `reduced` instead.
  const reduced = useReducedMotion();
  const firedRef = useRef<Set<SceneAudioCueWin>>(new Set());
  const T = VB_TOKENS;

  // Map wall time to round time. In reduced-motion mode we freeze on the
  // post-cashout peak frame; the same `state` is consumed by both branches
  // below so audio cues and banners stay coherent.
  const localT = reduced
    ? 10.5
    : lerp(7.6, 11.0, clamp(duration > 0 ? t / duration : 0, 0, 1));
  const state = computeRoundState(localT, "win", VB_DEFAULT_CASHOUT_T);
  // Post-patch the multiplier so the banner reflects the player's actual
  // payout, not the design's hardcoded ~4.5×.
  state.cashoutMultiplier = multiplier;
  state.multiplier = multiplier;

  // ----- Audio cue dispatch (fire each cue exactly once per mount) -----
  // Skip in reduced-motion mode — the static frame doesn't need a soundtrack.
  useEffect(() => {
    if (reduced) return;
    const fired = firedRef.current;
    if (state.cashoutFlash > 0 && !fired.has("cashoutFlash")) {
      fired.add("cashoutFlash");
      onAudioCue?.("cashoutFlash");
    }
    if (state.openProgress > 0.05 && !fired.has("doorSwing")) {
      fired.add("doorSwing");
      onAudioCue?.("doorSwing");
    }
    if (state.showResult === "win" && !fired.has("winSting")) {
      fired.add("winSting");
      onAudioCue?.("winSting");
    }
  }, [reduced, state.cashoutFlash, state.openProgress, state.showResult, onAudioCue]);

  // Reduced-motion peak frame (still image; no atmosphere, no cascade).
  if (reduced) {
    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          background: T.void0,
          width,
          height,
        }}
        data-reduced-motion="true"
      >
        <VaultDoor state={state} size={vaultSize} />
        <ResultBanner
          mode="win"
          multiplier={multiplier}
          progress={1}
          stake={stake}
          currency={currency}
        />
      </div>
    );
  }

  // ----- Banner appearance progress (slide-up over 0.5s) -----
  const bannerProgress = state.showResult === "win"
    ? clamp((localT - (state.cashoutT + 1.0)) / 0.5, 0, 1)
    : 0;

  const outerStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    background: T.void0,
    filter: state.desaturate ? "saturate(0.15) brightness(0.85)" : "none",
    transition: "filter 200ms",
    width,
    height,
  };

  const haloStyle: CSSProperties = {
    position: "absolute",
    left: "50%",
    top: "50%",
    width: vaultSize * 1.6,
    height: vaultSize * 1.6,
    transform: "translate(-50%, -50%)",
    background: state.openMode === "win"
      ? `radial-gradient(circle, ${T.gold4}55, ${T.gold2}22 30%, transparent 60%)`
      : `radial-gradient(circle, ${T.steel4}33, transparent 60%)`,
    filter: "blur(40px)",
    opacity: state.openMode ? state.openProgress : 0.55,
    pointerEvents: "none",
  };

  const lightTunnelStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    background: `radial-gradient(ellipse 40% 30% at 50% 50%, ${T.gold5}cc, ${T.gold3}55 40%, transparent 70%)`,
    mixBlendMode: "screen",
    opacity: state.openProgress,
    pointerEvents: "none",
  };

  const cashoutFlashStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    background: `radial-gradient(circle at 50% 50%, ${T.green2}cc, ${T.green2}33 30%, transparent 60%)`,
    mixBlendMode: "screen",
    opacity: state.cashoutFlash,
    pointerEvents: "none",
  };

  return (
    <div style={outerStyle} data-scene="win">
      {/* Mood layer (god-rays, dust, lens flare) */}
      <Atmosphere
        t={localT}
        tension={state.tension}
        lensX={0.62}
        lensY={0.42}
        hue="warm"
      />

      <CameraBreathe t={localT} intensity={0.7 + state.tension * 0.5}>
        {/* Halo behind the door */}
        <div style={haloStyle} />

        {/* Light tunnel during open */}
        {state.openMode === "win" && state.openProgress > 0.2 && (
          <div style={lightTunnelStyle} />
        )}

        {/* The vault door itself */}
        <VaultDoor state={state} size={vaultSize} />

        {/* Cashout flash (radial green wash) */}
        {state.cashoutFlash > 0 && <div style={cashoutFlashStyle} />}

        {/* Gold cascade once door cracks open ~30%. Sibling Particles API
            requires `t`, `centerX`, `centerY` — flagged in handoff summary. */}
        {state.openProgress > 0.3 && (
          <GoldCascade
            progress={(state.openProgress - 0.3) * 1.4}
            t={localT}
            centerX={width / 2}
            centerY={height / 2}
            count={70}
          />
        )}
      </CameraBreathe>

      {/* Result banner — single source of truth for the cashout label,
          multiplier, and BTC payout. Sits at the top of the modal, clear of
          the gold halo radiating from the door's centre. */}
      {bannerProgress > 0 && (
        <ResultBanner
          mode="win"
          multiplier={multiplier}
          progress={bannerProgress}
          stake={stake}
          currency={currency}
        />
      )}
    </div>
  );
}

// ----- Result banner -----
// Source: Scenes.jsx:181-223. Slide-up + drop-shadow per outcome palette.
interface ResultBannerProps {
  mode: "win";
  multiplier: number;
  /** 0..1 — slide-in / fade-in progress. */
  progress: number;
  stake: number;
  currency: string;
}

function ResultBanner({
  multiplier,
  progress,
  stake,
  currency,
}: ResultBannerProps): JSX.Element {
  const T = VB_TOKENS;
  const yOffset = (1 - progress) * 20;
  const style: CSSProperties = {
    position: "absolute",
    left: "50%",
    top: "12%",
    transform: `translate(-50%, ${yOffset}px)`,
    opacity: progress,
    pointerEvents: "none",
    textAlign: "center",
    padding: "14px 32px",
    borderRadius: 14,
    background: "linear-gradient(180deg, rgba(6,8,15,0.55), rgba(6,8,15,0))",
    backdropFilter: "blur(2px)",
    filter: `drop-shadow(0 0 24px ${T.green2}) drop-shadow(0 0 40px ${T.green2}aa)`,
  };
  const labelStyle: CSSProperties = {
    fontFamily: VB_FONTS.ui,
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 6,
    color: T.green3,
  };
  const multStyle: CSSProperties = {
    fontFamily: VB_FONTS.display,
    fontSize: 64,
    lineHeight: 1,
    marginTop: 6,
    color: T.green2,
    letterSpacing: -1,
  };
  const subStyle: CSSProperties = {
    fontFamily: VB_FONTS.mono,
    fontSize: 12,
    marginTop: 6,
    color: T.green3,
  };
  const btcAmount = stake * multiplier;
  return (
    <div style={style}>
      <div style={labelStyle}>CASHED OUT</div>
      <div style={multStyle}>{formatMultiplier(multiplier)}</div>
      <div style={subStyle}>+{formatCryptoCompact(btcAmount, currency)} {currency}</div>
    </div>
  );
}
